import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import * as ExifReader from 'exifreader';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { dotenv } from '@utils/constants/env';
import amplifyConfig from '../../amplifyconfiguration.json';

interface CognitoConfig {
  region: string;
  identityPoolId: string;
  userPoolId: string;
}

// Utility to extract GPS location from image file using exifreader

/**
 * Extracts GPS location (latitude, longitude) from an image File using exifreader
 * @param {File} file - Image file to extract location from
 * @returns {Promise<{ latitude: number; longitude: number } | null>} - Location or null if not found
 */
export const getImageLocation = async (
  file: ArrayBuffer
): Promise<{ latitude: number; longitude: number } | null> => {
  const tags = ExifReader.load(file);
  const latitude: any = tags.GPSLatitude?.description;
  const longitude: any = tags.GPSLongitude?.description;
  const latitudeRef: any = tags.GPSLatitudeRef?.description;
  const longitudeRef: any = tags.GPSLongitudeRef?.description;

  const applyHemisphere = (value: number, ref: string | undefined) => {
    const absValue = Math.abs(value);
    const normalizedRef = ref?.toLowerCase();

    if (normalizedRef?.includes('south') || normalizedRef?.includes('west')) {
      const result = -absValue;
      return result;
    }
    return absValue;
  };

  const processedLatitude =
    latitudeRef !== undefined
      ? applyHemisphere(parseFloat(latitude), latitudeRef)
      : parseFloat(latitude);
  const processedLongitude =
    longitudeRef !== undefined
      ? applyHemisphere(parseFloat(longitude), longitudeRef)
      : parseFloat(longitude);

  const result = { latitude: processedLatitude, longitude: processedLongitude };
  return result;
};

/**
 * Extracts Cognito configuration from amplifyconfiguration.json
 * @returns {CognitoConfig} - Cognito configuration
 */
export const getCognitoConfigFromAmplify = (): CognitoConfig => {
  const region = amplifyConfig.aws_cognito_region || '';
  const userPoolId = amplifyConfig.aws_user_pools_id || '';
  const identityPoolId = amplifyConfig.aws_cognito_identity_pool_id || '';

  if (!region || !userPoolId || !identityPoolId) {
    throw new Error(
      `Cognito configuration not found. Found: region=${region}, userPoolId=${userPoolId}, identityPoolId=${identityPoolId}`
    );
  }

  return {
    region,
    identityPoolId,
    userPoolId
  };
};

/**
 * Alternative: Get configuration from environment variables or external source
 * @param {any} configData - Configuration data (optional, can be from API)
 * @returns {CognitoConfig} - Cognito configuration
 */
export const getCognitoConfig = (configData?: any): CognitoConfig => {
  // If no config data provided, get from Amplify config file
  if (!configData) {
    return getCognitoConfigFromAmplify();
  }

  // Otherwise extract from provided data
  const data = configData?.data || configData;

  return {
    region: data?.cognitoRegion || data?.COGNITO_REGION || '',
    identityPoolId: data?.identityPoolId || data?.IDENTITY_POOL_ID || '',
    userPoolId: data?.userPoolId || data?.COGNITO_USER_POOL_ID || ''
  };
};

/**
 * Uploads a file to S3 using Cognito authentication
 * @param {File} file - File object to upload
 * @param {string} idToken - Cognito ID token (JWT) from logged-in user
 * @param {string} userId - User ID for folder structure
 * @param {string} folder - Optional folder path in S3 (default: 'profile')
 * @param {string} customPath - Optional custom path override
 * @param {Record<string, any>} metadata - Metadata to attach to the file
 * @returns {Promise<string>} - Uploaded file URL
 */
export const uploadToS3 = async (
  file: File,
  idToken: string,
  userId?: string,
  folder: string = 'profile',
  customPath?: string,
  metadata?: Record<string, any>
): Promise<string> => {
  if (!file) throw new Error('No file provided');
  if (!idToken) throw new Error('Missing Cognito ID token');
  if (!userId) throw new Error('Missing user ID');

  const { region, identityPoolId, userPoolId } = getCognitoConfig();

  // 🔐 Create S3 client with temporary credentials
  const s3 = new S3Client({
    region,
    credentials: fromCognitoIdentityPool({
      identityPoolId,
      logins: {
        [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken
      },
      clientConfig: { region }
    })
  });

  // 🧠 Unique file path
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const key = customPath
    ? `${customPath}${timestamp}_${sanitizedFileName}`
    : `${dotenv.ENV}/users/${userId}/${folder}/${timestamp}_${sanitizedFileName}`;

  // Convert File to ArrayBuffer for browser compatibility
  const fileBuffer = await file.arrayBuffer();
  const location = file?.type?.includes('image')
    ? await getImageLocation(fileBuffer)
    : null;

  // Convert metadata values to strings (S3 requirement)
  const stringifiedMetadata: Record<string, string> = {};
  if (metadata) {
    Object.entries(metadata).forEach(([key, value]) => {
      stringifiedMetadata[key] = String(value);
    });
  }
  // Always include fileName in metadata
  stringifiedMetadata['fileName'] = file.name;
  if (location) {
    stringifiedMetadata['location'] =
      location.latitude + ',' + location.longitude;
  }

  // 🧾 Prepare the upload command
  const command = new PutObjectCommand({
    Bucket: dotenv.BUCKET,
    Key: key,
    Body: new Uint8Array(fileBuffer),
    ContentType: file.type || 'application/octet-stream',
    Metadata: stringifiedMetadata
  });

  try {
    console.log('Sending PutObjectCommand to S3...');
    // 🚀 Upload to S3
    const response = await s3.send(command);
    console.log('S3 Upload Response:', response);

    // Return the full URL
    const url = `https://${dotenv.BUCKET}.s3.${region}.amazonaws.com/${key}`;
    return url;
  } catch (error: any) {
    throw new Error(`Failed to upload file: ${error.message || error}`);
  }
};

/**
 * Uploads multiple photos to S3 with progress tracking
 * @param {File[]} files - Array of File objects to upload
 * @param {string} idToken - Cognito ID token (JWT) from logged-in user
 * @param {string} userId - User ID for folder structure
 * @param {string} folder - Folder name (e.g., 'photos', 'videos', 'recipes')
 * @param {Function} onProgress - Callback function (currentIndex, total, fileName)
 * @param {string} subscriberId - Subscriber ID
 * @param {Record<string, any>} metadata - Metadata to attach to files
 * @returns {Promise<string[]>} - Array of uploaded file URLs
 */
export const uploadMultiplePhotos = async (
  files: File[],
  idToken: string,
  userId: string,
  folder: string,
  onProgress?: (current: number, total: number, fileName: string) => void,
  subscriberId?: string,
  metadata?: Record<string, any>
): Promise<string[]> => {
  if (!files || files.length === 0) throw new Error('No files provided');
  if (!idToken) throw new Error('Missing Cognito ID token');
  if (!userId) throw new Error('Missing user ID');

  const uploadedUrls: string[] = [];
  const customPath = `${dotenv.ENV}/temp/${subscriberId}/${folder}/`;

  try {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Upload file
      const url = await uploadToS3(
        file,
        idToken,
        userId,
        folder,
        customPath,
        metadata
      );
      uploadedUrls.push(url);

      // Trigger progress callback
      if (onProgress) {
        onProgress(i + 1, files.length, file.name);
      }
    }

    return uploadedUrls;
  } catch (error: any) {
    throw new Error(`Failed to upload photos: ${error.message || error}`);
  }
};

/**
 * Uploads multiple files to S3 using Cognito authentication
 * @param {File[]} files - Array of File objects to upload
 * @param {string} idToken - Cognito ID token (JWT) from logged-in user
 * @param {string} userId - User ID for folder structure
 * @param {string} folder - Optional folder path in S3 (default: 'attachments')
 * @param {Record<string, any> | ((file: File, index: number) => Record<string, any>)} metadata - Metadata for all files or function to generate per-file metadata
 * @param {string} subscriberId - Optional subscriber ID for custom path
 * @returns {Promise<string[]>} - Array of uploaded file URLs
 */
export const uploadMultipleToS3 = async (
  files: File[],
  idToken: string,
  userId?: string,
  folder: string = 'attachments',
  metadata?:
    | Record<string, any>
    | ((file: File, index: number) => Record<string, any>),
  subscriberId?: string
): Promise<string[]> => {
  if (!files || files.length === 0) throw new Error('No files provided');
  if (!idToken) throw new Error('Missing Cognito ID token');

  try {
    // Generate custom path if subscriberId is provided
    const customPath = subscriberId
      ? `${dotenv.ENV}/temp/${subscriberId}/${folder}/`
      : undefined;

    // Upload all files sequentially to better track errors
    const uploadedUrls: string[] = [];

    for (let index = 0; index < files.length; index++) {
      const file = files[index];

      const fileMetadata =
        typeof metadata === 'function' ? metadata(file, index) : metadata;

      const url = await uploadToS3(
        file,
        idToken,
        userId,
        folder,
        customPath,
        fileMetadata
      );

      uploadedUrls.push(url);
    }

    return uploadedUrls;
  } catch (error: any) {
    throw new Error(`Failed to upload files: ${error.message || error}`);
  }
};

/**
 * Validates if file is an image or video and within size limit
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum file size in MB (default: 50MB for videos)
 * @returns {boolean} - True if valid
 */
export const validateImageFile = (
  file: File
): { valid: boolean; error?: string } => {
  const validTypes = [
    // Images
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/webp',
    'image/gif',

    // Videos
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/avi',
    'application/x-troff-msvideo',
    'video/x-matroska',
    'video/webm',

    // Audio
    'audio/mpeg',
    'audio/wav',
    'audio/mp3',
    'audio/ogg',
    'audio/webm',

    // Documents
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'text/plain', // .txt
    'application/rtf' // .rtf
  ];
  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error:
        'Invalid file type. Only images (JPEG, PNG, WEBP, GIF), videos (MP4, MOV, AVI, MKV, WEBM), audio (MP3, WAV, OGG, WEBM), and documents (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, RTF) are allowed.'
    };
  }

  return { valid: true };
};

/**
 * Validates general files including documents, images, and PDFs
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum file size in MB (default: 10MB)
 * @returns {object} - Validation result
 */

const memoryValidTypes = [
  // Images
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/webp',
  'image/gif',

  // Videos
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/avi',
  'application/x-troff-msvideo',
  'video/x-matroska',
  'video/webm'
];

export const validateFile = (
  file: File,
  module?: string
): { valid: boolean; error?: string } => {
  const validTypes = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/webp',
    'image/gif',
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/avi',
    'application/x-troff-msvideo',
    'video/x-matroska',
    'video/webm',
    'audio/mpeg',
    'audio/wav',
    'audio/mp3',
    'audio/ogg',
    'audio/webm',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];
  const allowedTypes = module === 'memories' ? memoryValidTypes : validTypes;

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error:
        module === 'memories'
          ? 'Invalid file type. Only images (JPEG, PNG, JPG, WEBP, GIF) and videos (MP4, MOV, AVI, MKV, WEBM) are allowed.'
          : 'Invalid file type. Only images (JPEG, PNG, JPG, WEBP, GIF), videos (MP4, MOV, AVI, MKV, WEBM), audio files (MP3, WAV, OGG, WEBM), PDFs, and documents (DOC, DOCX, TXT) are allowed.'
    };
  }

  return { valid: true };
};

/**
 * Validates multiple files
 * @param {File[]} files - Files to validate
 * @param {number} maxSizeMB - Maximum file size in MB per file
 * @param {number} maxFiles - Maximum number of files allowed
 * @param {boolean} allowDocuments - Whether to allow documents (default: false, only images/videos)
 * @returns {object} - Validation result
 */
export const validateMultipleFiles = (
  files: File[],
  maxFiles: number = 5,
  allowDocuments: boolean = false
): { valid: boolean; error?: string } => {
  if (!files || files.length === 0) {
    return { valid: false, error: 'No files selected' };
  }

  if (maxFiles > 1 && files.length > maxFiles) {
    return { valid: false, error: `Maximum ${maxFiles} files allowed` };
  }

  for (const file of files) {
    const validation = allowDocuments
      ? validateFile(file)
      : validateImageFile(file);
    if (!validation.valid) {
      return validation;
    }
  }

  return { valid: true };
};
